import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import CheckIcon from '../../../assets/icons/check.svg?react';
import MinusIcon from '../../../assets/icons/minus.svg?react';
import DotIcon from '../../../assets/icons/dot-indicator.svg?react';
import HourglassIcon from '../../../assets/icons/hourglass.svg?react';
import ErrorIcon from '../../../assets/icons/error.svg?react';
import QuestionIcon from '../../../assets/icons/question.svg?react';
import { Tag } from '../../UI';
import { formatDateTime } from '../../../utils/dateUtils';
import type { PlanningIssue, WeekCalculationDisplayStatus, WeekKpi } from './types';
import styles from './WeekKpiCell.module.css';

interface StatusRowProps {
  kpi: WeekKpi;
  // When provided the status chip becomes a button. What it does depends on the status — recalculate
  // the week, or retry loading the statuses when they are UNKNOWN — so the wording of the hint and
  // the aria-label comes from the table below rather than from here.
  onStatusClick?: (weekNumber: number) => void;
}

/**
 * Everything the chip needs per status, in one table.
 *
 * One entry per status rather than four parallel switches: `Record` gives the same compile-time
 * exhaustiveness — a new status fails to typecheck until it is added here — without four unreachable
 * `never` guards, and a status's whole appearance is legible in a single line.
 *
 * Glyph heights live in WeekKpiCell.module.css, tuned per-glyph so the marks look the same size.
 */
interface StatusPresentation {
  readonly Icon: ComponentType<SVGProps<SVGSVGElement>>;
  readonly iconClass: string;
  readonly variantClass: string;
  readonly translationKey: string;
  // Tooltip line and accessible name for the chip's action, used only when it is clickable.
  readonly clickHintKey: string;
  readonly clickAriaKey: string;
}

const STATUS_PRESENTATION: Record<WeekCalculationDisplayStatus, StatusPresentation> = {
  LOCKED: {
    Icon: DotIcon,
    iconClass: styles.statusIconDot,
    variantClass: styles.tagTinted,
    translationKey: 'gantt-status-locked',
    clickHintKey: 'gantt-status-recalculate-hint',
    clickAriaKey: 'gantt-recalculate-week-aria'
  },
  CALCULATED: {
    Icon: CheckIcon,
    iconClass: styles.statusIconCheck,
    variantClass: '',
    translationKey: 'gantt-status-calculated',
    clickHintKey: 'gantt-status-recalculate-hint',
    clickAriaKey: 'gantt-recalculate-week-aria'
  },
  CALCULATING: {
    Icon: HourglassIcon,
    // The only status whose glyph animates, hence the extra class.
    iconClass: `${styles.statusIconHourglass} ${styles.calculatingIcon}`,
    variantClass: styles.calculatingTag,
    translationKey: 'gantt-status-calculating',
    clickHintKey: 'gantt-status-recalculate-hint',
    clickAriaKey: 'gantt-recalculate-week-aria'
  },
  NOT_CALCULATED: {
    Icon: MinusIcon,
    iconClass: styles.statusIconMinus,
    variantClass: '',
    translationKey: 'gantt-status-not-calculated',
    clickHintKey: 'gantt-status-recalculate-hint',
    clickAriaKey: 'gantt-recalculate-week-aria'
  },
  FAILED: {
    Icon: ErrorIcon,
    iconClass: styles.statusIconError,
    variantClass: styles.tagError,
    translationKey: 'gantt-status-failed',
    clickHintKey: 'gantt-status-recalculate-hint',
    clickAriaKey: 'gantt-recalculate-week-aria'
  },
  UNKNOWN: {
    Icon: QuestionIcon,
    iconClass: styles.statusIconUnknown,
    // Neutral, not an error tint: not knowing is our problem, not the week's.
    variantClass: '',
    translationKey: 'gantt-status-unknown',
    // The one status whose action is not a recalculation: nothing is known about the week yet, so the
    // chip offers to load the statuses again instead.
    clickHintKey: 'gantt-status-retry-hint',
    clickAriaKey: 'gantt-status-retry-aria'
  }
};

interface PlanningIssueKeys {
  readonly label: string;
  readonly tooltip: string;
}

const planningIssueKeys = (code: PlanningIssue['code']): PlanningIssueKeys => {
  switch (code) {
    case 'UPLANLAGTE':
      return { label: 'gantt-unplanned', tooltip: 'gantt-unplanned-tooltip' };
    default: {
      const _exhaustive: never = code;
      throw new Error(`Unhandled PlanningIssueCode: ${_exhaustive as string}`);
    }
  }
};

export const StatusRow = ({ kpi, onStatusClick }: StatusRowProps) => {
  const { t } = useTranslation();
  const { calculation } = kpi;
  const { Icon, iconClass, variantClass, translationKey, clickHintKey, clickAriaKey } =
    STATUS_PRESENTATION[calculation.status];
  // Only some states carry these, so they are read by narrowing rather than destructured up front.
  const finishedAt = 'finishedAt' in calculation ? calculation.finishedAt : null;
  const errorMessage = 'errorMessage' in calculation ? calculation.errorMessage : null;

  // The timestamp is the only honest freshness signal available: a finished calculation says nothing
  // about whether the plan changed afterwards, so the planner is shown when it ran and judges for
  // themselves. The solver's error text is developer-facing and deliberately untranslated.
  const statusTooltip = (
    <div className={styles.tagTooltip}>
      {t(translationKey)}
      {finishedAt && (
        <div>{t('gantt-status-calculated-at', { at: formatDateTime(finishedAt) })}</div>
      )}
      {errorMessage && <div className={styles.tooltipDetail}>{errorMessage}</div>}
      {onStatusClick && <div>{t(clickHintKey)}</div>}
    </div>
  );

  return (
    <div className={styles.statusRow}>
      <Tag
        label={<Icon className={iconClass} aria-hidden="true" />}
        size="small"
        tooltip={statusTooltip}
        onClick={onStatusClick ? () => { onStatusClick(kpi.weekNumber); } : undefined}
        clickAriaLabel={t(clickAriaKey, { week: kpi.weekNumber })}
        className={`${styles.statusTag} ${variantClass}`}
      />
      {kpi.planningIssues.map((issue) => {
        const keys = planningIssueKeys(issue.code);
        const label =
          issue.count !== undefined ? `${t(keys.label)} (${issue.count})` : t(keys.label);
        return (
          <Tag
            key={issue.code}
            label={label}
            size="small"
            tooltip={<div className={styles.tagTooltip}>{t(keys.tooltip)}</div>}
            className={styles.tagError}
          />
        );
      })}
    </div>
  );
};
